import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpSentEvent,
  HttpHeaderResponse,
  HttpProgressEvent,
  HttpResponse,
  HttpUserEvent,
  HttpErrorResponse
} from '@angular/common/http';

import { AuthService } from './auth.service';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, switchMap, finalize, filter, take } from 'rxjs/operators';
import { ICurrentUser } from '../models/ICurrentUser.model';
import { Router } from '@angular/router';


@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, private router: Router) {
    this.handleMicrofrontendEvents();
  }

  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  intercept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<
    | HttpSentEvent
    | HttpHeaderResponse
    | HttpProgressEvent
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | HttpResponse<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | HttpUserEvent<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any
  > {
    return next
      .handle(this.addTokenToRequest(request, this.authService.getAccesToken()))
      .pipe(
        catchError(err => {
          if (request.url.endsWith('token')) {
            return throwError(this.mapResponse(err));
          }

          if (err instanceof HttpErrorResponse) {
            // Angular's HttpClient wraps a successful 200 OK response as an
            // HttpErrorResponse when the body isn't valid JSON (e.g. the backend
            // returns 200 with an empty body for "no data"/"no pending item" cases,
            // like the post-login notifications popup check). This is not a real
            // failure — treat it as a successful empty response instead of letting
            // it fall through to the generic error mapper, which was surfacing a
            // false "OK" / "Entendido" snackbar and forcing users to log in twice.
            if (err.status === 200) {
              return of(new HttpResponse({ status: 200, body: null, url: request.url }));
            }

            switch ((<HttpErrorResponse>err).status) {
              case 401:
                return this.handle401Error(request, next);
            }
          }

          return throwError(this.mapResponse(err));
        })
      );
  }

  private addTokenToRequest(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: HttpRequest<any>,
    token: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): HttpRequest<any> {
    if (request.url.endsWith('token') || request.url.endsWith('ResetPassword') || request.url.endsWith('GenerateOTP') || request.url.endsWith('QR')) {
      return request;
    }

    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((user: ICurrentUser) => {
          if (user) {
            this.tokenSubject.next(user.access_token);
            return next.handle(
              this.addTokenToRequest(request, user.access_token)
            );
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return <any>this.authService.logout();
        }),
        catchError(err => {
          this.authService.notifySessionExpired();
          throw err;
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addTokenToRequest(request, token));
        })
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapResponse(err: any): any {
    // status lets screens tell a not found (404) from any other failure (ECM-15942); 0 when there was no response.
    // error is the response body as received (exports' 422 reason codes, PDF analyzer); none without a response.
    const response = {
      code: 'UndefinedError',
      description : '',
      status: err?.status ?? 0,
      error: err?.status > 0 ? err.error : undefined
    };

    if (err.error && err.error.modelState && err.error.modelState.error && err.error.modelState.error.length > 0) {
      response.code = err.error.modelState.error[0];
      response.description  = err.error.modelState.error[0];
      return response;
    }

    if (err.error && err.error.error) {
      if (err.error.error !== 'error') {
        response.code = err.error.error;
        response.description = err.error.error_description;
        return response;
      } else if (err.error.error_description) {
        response.code = err.error.error_description;
        return response;
      }
    }
    if (err.error && err.error.modelState && Object.keys(err.error.modelState) && Object.keys(err.error.modelState).length > 0) {
        Object.keys(err.error.modelState).forEach(propertyName => {
          response.code = propertyName.toUpperCase();
          if (err.error.modelState[propertyName] && err.error.modelState[propertyName].length > 0) {
            response.description = err.error.modelState[propertyName][0];
          }
        });

        return response;
      }

    if (err.status > 0) {
      response.code = '_' + err.status;
      // Angular defaults statusText to 'OK' when the server sends none (always on HTTP/2),
      // which surfaced real errors as an "OK" toast. Leave it empty so the generic message is used.
      response.description = err.statusText && err.statusText !== 'OK' ? err.statusText : '';
      return response;
    }

    return response;
  }

  private handleMicrofrontendEvents() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.addEventListener("refreshtoken", ({ detail }: any) => {
      const { callbackFn } = detail;
      const sendResponse = (success: boolean) => {
        try {
          callbackFn(success);
        } catch (error) {
          console.error("Error executing callbackFn:", error);
        }
      };

      if (!this.isRefreshingToken) {
        this.isRefreshingToken = true;

        // Reset here so that the following requests wait until the token
        // comes back from the refreshToken call.
        this.tokenSubject.next(null);

        this.authService
          .refreshToken()
          .pipe(finalize(() => (this.isRefreshingToken = false)))
          .subscribe({
            next: (user) => {
              if (user) {
                this.tokenSubject.next(user.access_token);
                sendResponse(true);
                return;
              }
              sendResponse(false);
              this.authService.logout();
            },
            error: (err) => {
              sendResponse(false);
              this.authService.logout(true);
              throw err;
            },
          });
      } else {
        this.tokenSubject
          .pipe(
            filter((token) => token != null),
            take(1)
          )
          .subscribe(() => sendResponse(true));
      }
    });
  }
}
